import { useState, useEffect } from 'react';
import { db } from "../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import * as theme from '../../theme';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import ErrorDisplay from '../../components/ErrorDisplay';
import TextField from "@mui/material/TextField";
import FormControl from '@mui/material/FormControl';
import { InputLabel, Select, MenuItem } from '@mui/material';
import AddableList from '../../components/AddableList';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useAuth } from "../../contexts/AuthContext";
import Heading from '../../components/Heading';
import { Timestamp } from 'firebase/firestore';
import Grid from '@mui/material/Grid';
import { TherapiesContext } from '../../contexts/TherapiesContext';

export default function EditOnboardingForm() {
  const [practitioner, setPractitioner] = useState({});
  const [formData, setFormData] = useState({
    clientCount: '',
    monthCount: '',
    supplementCompanies: [],
    conditions: [],
    leadsGroupDetox: null,
    groupDetox: null,
    specificTherapies: [],
    additionalTherapies: [],
    scanningRequests: [],
    specialRequests: [],
    questions: [],
    detoxCohorts: [],
    comments: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    if (id && currentUser.role !== 'practitioner') {
      getPractitioner();
    } else {
      navigate(`/practitioners/${id}/onboarding`);
    }
  }, [id, currentUser.role]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBooleanChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const addItem = (field, value) => {
    // Only wrap value in object for supplementCompanies and specificTherapies
    const isObjectField = field === 'supplementCompanies' || field === 'specificTherapies' || field === 'additionalTherapies';
    const newValue = isObjectField ? value : value.name || value; // Extract string if value is an object
    setFormData({
      ...formData,
      [field]: [...formData[field], newValue]
    });
  };

  const removeItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const handleItemNoteChange = (field, index, note) => {
    const updated = formData[field].map((item, i) => i === index ? { ...item, note } : item);
    setFormData({ ...formData, [field]: updated });
  };

  const handleTherapyToggle = (therapy) => {
    const isSelected = formData.specificTherapies.some(t => t.name === therapy);
    if (isSelected) {
      setFormData({
        ...formData,
        specificTherapies: formData.specificTherapies.filter(t => t.name !== therapy)
      });
    } else {
      const existing = formData.specificTherapies.find(t => t.name === therapy);
      setFormData({
        ...formData,
        specificTherapies: [...formData.specificTherapies, existing || { name: therapy, isAdded: false, note: '' }]
      });
    }
  };

  const handleCommentChange = (section, value) => {
    setFormData({
      ...formData,
      comments: { ...formData.comments, [section]: value }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  const confirmSave = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setError("");

    try {
      const practitionerRef = doc(db, 'practitioners', id);
      await updateDoc(practitionerRef, {
        'onboarding.form': {
          ...formData,
          dateModified: Timestamp.now()
        }
      });
      navigateBack();
    } catch (err) {
      setError("Failed to save changes: " + err.message);
    }

    setLoading(false);
  };

  function navigateBack() {
    navigate(`/practitioners/${id}/onboarding`);
  }

  async function getPractitioner() {
    try {
      const practitionerRef = doc(db, "practitioners", id);
      const docSnap = await getDoc(practitionerRef);
      const practitionerData = docSnap.data();
      setPractitioner(practitionerData);
      if (practitionerData?.onboarding?.form) {
        const form = practitionerData.onboarding.form;
        let supps = form.supplementCompanies || [];
        if (typeof supps === 'string') {
          supps = supps.split(',').map(s => ({ name: s.trim(), note: '', isAdded: false })).filter(s => s.name);
        } else {
          supps = supps.map(s => ({ ...s, note: s.note || '', isAdded: s.isAdded ?? false }));
        }
        let specTherapies = form.specificTherapies || [];
        if (typeof specTherapies === 'string') {
          specTherapies = specTherapies.split(',').map(s => ({ name: s.trim(), note: '', isAdded: false })).filter(s => s.name);
        } else {
          specTherapies = specTherapies.map(t => ({ ...t, note: t.note || '', isAdded: t.isAdded ?? false }));
        }
        let addTherapies = form.additionalTherapies || [];
        if (typeof addTherapies === 'string') {
          addTherapies = addTherapies.split(',').map(s => ({ name: s.trim(), note: '', isAdded: false })).filter(s => s.name);
        } else {
          addTherapies = addTherapies.map(t => ({ ...t, name: t.name || t, note: t.note || '', isAdded: t.isAdded ?? false }));
        }
        setFormData({
          ...formData,
          ...form,
          supplementCompanies: supps,
          specificTherapies: specTherapies,
          additionalTherapies: addTherapies,
          comments: form.comments || {}
        });
      }
    } catch (err) {
      console.log("Failed to get practitioner: " + err.message);
    }
  }

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Container.flexColumn}>
        <Heading
          title={`${practitioner.firstName} ${practitioner.lastName}`}
          onClick={() => navigate(`/practitioners/${id}/onboarding`)}
        />
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <Heading 
            title={'Edit Onboarding Form'} 
            subHeading={true}
          />
          <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
            <Box sx={{ marginBottom: 2 }}>
              <ErrorDisplay
                message={error}
                type={'error'}
                clearMessage={clearError}
              />
            </Box>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ ...theme.FullForm.form, gap: 8 }}
            >
              {/* General Section */}
              <Box sx={{ ...theme.FullForm.form, gap: 2, ...theme.FullForm.goldBorder }}>
                <div style={{ fontWeight: 600 }}>General</div>
                <TextField
                  label={'Clients Per Week'}
                  name="clientCount"
                  value={formData.clientCount}
                  onChange={handleChange}
                  variant="filled"
                  disabled={loading}
                />
                <TextField
                  label={'Client Scans Per Month'}
                  name="monthCount"
                  value={formData.monthCount}
                  onChange={handleChange}
                  variant="filled"
                  disabled={loading}
                />
                <TextField
                  label="TruBalance Comments"
                  value={formData.comments.general || ''}
                  onChange={(e) => handleCommentChange('general', e.target.value)}
                  variant="filled"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={loading}
                />
              </Box>

              {/* Supplement Companies Section */}
              <Box sx={{ ...theme.FullForm.form, gap: 2, ...theme.FullForm.goldBorder }}>
                <div style={{ fontWeight: 600 }}>Supplement Companies</div>
                <div style={{ paddingLeft: 6 }}>List all supplement companies you use (if only using a single product from a company, just list that product instead of the company):</div>
                <AddableList
                  label="New Supplement Company"
                  items={formData.supplementCompanies}
                  onAdd={(value) => addItem('supplementCompanies', value)}
                  onRemove={(index) => removeItem('supplementCompanies', index)}
                  disabled={loading}
                  isObject={true}
                  enableNotes={true}
                  onNoteChange={(index, note) => handleItemNoteChange('supplementCompanies', index, note)}
                />
                <TextField
                  label="TruBalance Comments"
                  value={formData.comments.supplements || ''}
                  onChange={(e) => handleCommentChange('supplements', e.target.value)}
                  variant="filled"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={loading}
                />
              </Box>

              {/* Conditions Section */}
              <Box sx={{ ...theme.FullForm.form, gap: 2, ...theme.FullForm.goldBorder }}>
                <div style={{ fontWeight: 600 }}>Conditions Treated</div>
                <div style={{ paddingLeft: 6 }}>What types of conditions/health concerns do you work with?</div>
                <AddableList
                  label="New Condition"
                  items={formData.conditions}
                  onAdd={(value) => addItem('conditions', value)}
                  onRemove={(index) => removeItem('conditions', index)}
                  isObject={false}
                  disabled={loading}
                />
                <TextField
                  label="TruBalance Comments"
                  value={formData.comments.conditions || ''}
                  onChange={(e) => handleCommentChange('conditions', e.target.value)}
                  variant="filled"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={loading}
                />
              </Box>

              {/* Group Detox Section */}
              <Box sx={{ ...theme.FullForm.form, ...theme.FullForm.goldBorder }}>
                <div style={{ fontWeight: 600 }}>Group Detox/Cohorts</div>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Do you lead group detox programs/cohorts?</InputLabel>
                  <Select
                    name="leadsGroupDetox"
                    value={formData.leadsGroupDetox === null ? '' : formData.leadsGroupDetox}
                    onChange={(e) => handleBooleanChange('leadsGroupDetox', e.target.value === 'true')}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
                {formData.leadsGroupDetox === true && (
                  <FormControl variant="filled" fullWidth sx={{ marginTop: 2 }}>
                    <InputLabel>Would you want to include this in the program?</InputLabel>
                    <Select
                      name="groupDetox"
                      value={formData.groupDetox === null ? '' : formData.groupDetox}
                      onChange={(e) => handleBooleanChange('groupDetox', e.target.value === 'true')}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {formData.leadsGroupDetox === true && formData.groupDetox === true && (
                  <Box sx={{ ...theme.FullForm.form, gap: 2, marginTop: 2 }}>
                    <div style={{ paddingLeft: 6 }}>What type of detox cohort(s) do you provide?</div>
                    <AddableList
                      label="New Detox Cohort"
                      items={formData.detoxCohorts}
                      onAdd={(value) => addItem('detoxCohorts', value)}
                      onRemove={(index) => removeItem('detoxCohorts', index)}
                      isObject={false}
                      disabled={loading}
                    />
                  </Box>
                )}
                <TextField
                  label="TruBalance Comments"
                  value={formData.comments.groupDetox || ''}
                  onChange={(e) => handleCommentChange('groupDetox', e.target.value)}
                  variant="filled"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={loading}
                  sx={{ marginTop: 2 }}
                />
              </Box>

              {/* Therapies Section */}
              <Box sx={{ ...theme.FullForm.form, gap: 2, ...theme.FullForm.goldBorder }}>
                <div style={{ fontWeight: 600 }}>Therapies</div>
                <div style={{ paddingLeft: 6 }}>Select therapies or treatments to exclude from scans:</div>
                <Grid container spacing={1}>
                  {TherapiesContext.map((therapy) => (
                    <Grid item xs={12} sm={6} md={4} key={therapy}>
                      <Button
                        variant={formData.specificTherapies.some(t => t.name === therapy) ? "contained" : "outlined"}
                        color="primary"
                        onClick={() => handleTherapyToggle(therapy)}
                        disabled={loading}
                        sx={{
                          textTransform: 'none',
                          width: '100%',
                          fontSize: 12,
                          backgroundColor: formData.specificTherapies.some(t => t.name === therapy)
                            ? theme.Colors.Purple
                            : 'transparent',
                          color: formData.specificTherapies.some(t => t.name === therapy)
                            ? '#fff'
                            : theme.Colors.GrayDark,
                          borderColor: theme.Colors.GrayMedium,
                          '&:hover': {
                            backgroundColor: formData.specificTherapies.some(t => t.name === therapy)
                              ? theme.Colors.PurpleDark
                              : theme.Colors.GrayLight,
                          },
                        }}
                      >
                        {therapy}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
                <div style={{ paddingLeft: 6, marginTop: 2 }}>Are there specific therapies that you use at your clinic or refer people out to that are not included in the list above. If so, include in the list below.</div>
                <AddableList
                  label="New Additional Therapy"
                  items={formData.additionalTherapies}
                  onAdd={(value) => addItem('additionalTherapies', value)}
                  onRemove={(index) => removeItem('additionalTherapies', index)}
                  isObject={true}
                  disabled={loading}
                />
                <TextField
                  label="TruBalance Comments"
                  value={formData.comments.therapies || ''}
                  onChange={(e) => handleCommentChange('therapies', e.target.value)}
                  variant="filled"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={loading}
                />
              </Box>

              {/* Special Requests Section */}
              <Box sx={{ ...theme.FullForm.form, gap: 2, ...theme.FullForm.goldBorder }}>
                <div style={{ fontWeight: 600 }}>Special Requests</div>
                <div style={{ paddingLeft: 6 }}>Are there any special scanning requests or things you would like tested for your clients?</div>
                <AddableList
                  label="New Scanning Request"
                  items={formData.scanningRequests}
                  onAdd={(value) => addItem('scanningRequests', value)}
                  onRemove={(index) => removeItem('scanningRequests', index)}
                  isObject={false}
                  disabled={loading}
                />
                <div style={{ paddingLeft: 6 }}>Are there any special requests in regards to the report your clients will receive?</div>
                <AddableList
                  label="New Report Request"
                  items={formData.specialRequests}
                  onAdd={(value) => addItem('specialRequests', value)}
                  onRemove={(index) => removeItem('specialRequests', index)}
                  isObject={false}
                  disabled={loading}
                />
                <TextField
                  label="TruBalance Comments"
                  value={formData.comments.specialRequests || ''}
                  onChange={(e) => handleCommentChange('specialRequests', e.target.value)}
                  variant="filled"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={loading}
                />
              </Box>

              {/* Questions Section */}
              <Box sx={{ ...theme.FullForm.form, gap: 2, ...theme.FullForm.goldBorder }}>
                <div style={{ fontWeight: 600 }}>Questions for TruBalance</div>
                <AddableList
                  label="New Question"
                  items={formData.questions}
                  onAdd={(value) => addItem('questions', value)}
                  onRemove={(index) => removeItem('questions', index)}
                  isObject={false}
                  disabled={loading}
                />
                <TextField
                  label="TruBalance Comments"
                  value={formData.comments.questions || ''}
                  onChange={(e) => handleCommentChange('questions', e.target.value)}
                  variant="filled"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={loading}
                />
              </Box>

              <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ flex: 1 }}
                  onClick={() => navigateBack()}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ flex: 3 }}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Box>
            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
              open={loading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </div>
        </div>
      </div>
      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmSave}
        title="Confirm Save"
        description="Are you sure you want to save these changes?"
        confirmButtonText="Save"
      />
    </div>
  );
}